import React, { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';

import { Box, Button, Header, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { Nullable } from '@rbi-ctg/frontend';
import { IHistoricalOrder, ServiceMode } from '@rbi-ctg/menu';
import { ActionsheetContent, ActionsheetWrapper } from 'components/action-sheet';
import { useAppStateStatus } from 'components/ota-update-alert/use-app-state-status';
import useAuth from 'hooks/auth/use-auth';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import BrandLogoIcon from 'hooks/use-map/icons/store-marker-active';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { StorageKeys } from 'utils/local-storage';
import { isPickup } from 'utils/service-mode';

interface IConfirmLocationDrawer {
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  changeAddress: () => void;
  address: ReactNode;
}

const ConfirmLocationDrawer = React.memo(
  ({ isOpen, onDismiss, address, onConfirm, changeAddress }: IConfirmLocationDrawer) => {
    const { formatMessage } = useIntl();

    return (
      <ActionsheetWrapper onOpenEventMessage="Confirm Location" isOpen={isOpen} onClose={onDismiss}>
        <ActionsheetContent>
          <Box width="full" paddingX="$4">
            <Box alignItems="center">
              <BrandLogoIcon />
              <Header mt="$8" variant={'headerOne'}>
                {formatMessage({ id: 'confirmYourPickupAddress' })}
              </Header>
              <Text>{address}</Text>
            </Box>
            <Button mt="$10" onPress={onConfirm}>
              {formatMessage({ id: 'confirmAndOrder' })}
            </Button>
            <Button mt="$1" variant="ghost" onPress={changeAddress}>
              {formatMessage({ id: 'change' })}
            </Button>
          </Box>
        </ActionsheetContent>
      </ActionsheetWrapper>
    );
  }
);

export function useConfirmLocationDrawer__Experiment({
  serviceMode,
  orders,
  hasAddressSelected,
}: {
  serviceMode: Nullable<ServiceMode>;
  orders: IHistoricalOrder[];
  hasAddressSelected: boolean;
}): {
  shouldConfirmLocation: () => boolean;
  isDrawerOpen: boolean;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  isEnabled: boolean;
  setConfirmationWasDisplayedInSession: (newDataUpdate: boolean) => void;
} {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const appState = useAppStateStatus();
  const { isAuthenticated } = useAuth();

  const [
    confirmationWasDisplayedInSession,
    setConfirmationWasDisplayedInSession,
  ] = useLocalStorageState<boolean | null>({
    key: StorageKeys.CONFIRM_LOCATION_DRAWER_DISPLAYED,
    defaultReturnValue: false,
  });

  const { variant } = useExperiments();

  const isTreatment = variant(RunningExperiments.EnableConfirmLocationDrawerAtHome, 'treatment');

  const shouldConfirmLocation = () => {
    const currentServiceModeIsPickUp = isPickup(serviceMode);
    const lastOrderIsPickUp = orders.length && isPickup(orders[0].cart.serviceMode);
    const shouldDisplayStoreConfirmation =
      hasAddressSelected && currentServiceModeIsPickUp && lastOrderIsPickUp;
    return Boolean(shouldDisplayStoreConfirmation);
  };

  useEffect(() => {
    if ((appState === 'inactive' && !isDrawerOpen) || !isAuthenticated) {
      setConfirmationWasDisplayedInSession(false);
    }
  }, [
    appState,
    confirmationWasDisplayedInSession,
    isDrawerOpen,
    setConfirmationWasDisplayedInSession,
    isAuthenticated,
  ]);

  return {
    shouldConfirmLocation,
    isDrawerOpen,
    setIsDrawerOpen,
    isEnabled: isAuthenticated && isTreatment && !confirmationWasDisplayedInSession,
    setConfirmationWasDisplayedInSession,
  };
}

interface IConfirmLocationDrawer__Experiment {
  onDismiss: () => void;
  isOpen: boolean;
  onConfirm: () => void;
  changeAddress: () => void;
  address: ReactNode;
}

export const ConfirmLocationDrawer__Experiment = (props: IConfirmLocationDrawer__Experiment) => {
  const { variant } = useExperiments();
  const isTreatment = variant(RunningExperiments.EnableConfirmLocationDrawerAtHome, 'treatment');

  if (isTreatment) {
    return <ConfirmLocationDrawer {...props} />;
  }

  return null;
};
